import { useEffect, useState } from "react";
import api from "@/lib/api";
import Tip from "@/components/Tip";
import { CheckCircle2, Circle, Rocket, ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const BRAND_BLUE = "#004a99";
const BRAND_ORANGE = "#f37021";

const LABELS = {
  signature: "Save doctor signature",
  distribution: "Open Distribution Kit",
  voice_note: "Record first voice note",
  whatsapp_sent: "Send a WhatsApp message",
  prescription: "Generate first prescription",
  first_payment: "Receive first payment",
};

export default function OnboardingMeter({ clinicId, userId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const seen = localStorage.getItem(`pulse_onboarding_dismissed_${userId}`);
    if (seen === "1") setDismissed(true);
  }, [userId]);

  const load = async () => {
    if (!clinicId) return;
    try {
      const r = await api.get(`/clinics/${clinicId}/onboarding-status`);
      // Merge with client-side localStorage flag for "distribution"
      const distributionSeen = localStorage.getItem(`pulse_distribution_seen_${userId}`) === "1";
      const steps = r.data.steps.map(s => s.key === "distribution" ? { ...s, done: distributionSeen } : s);
      const completed = steps.filter(s => s.done).length;
      setData({ ...r.data, steps, completed, pct: Math.round(completed / steps.length * 100) });
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clinicId]);

  const goto = (path) => {
    if (path === "/distribution-kit") {
      localStorage.setItem(`pulse_distribution_seen_${userId}`, "1");
    }
    if (path.includes("?tab=")) {
      const [base, qs] = path.split("?");
      const tab = qs.split("=")[1];
      sessionStorage.setItem("pulse_jump_tab", tab);
      navigate(base);
    } else {
      navigate(path);
    }
  };

  const dismiss = () => { setDismissed(true); localStorage.setItem(`pulse_onboarding_dismissed_${userId}`, "1"); };

  if (!data || dismissed || data.completed === data.total) return null;

  const pct = data.pct;
  return (
    <div className="card-3d bg-white rounded-2xl p-4 sm:p-5 border-2 fade-up" style={{ borderColor: `${BRAND_BLUE}25` }} data-testid="onboarding-meter">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="icon-3d icon-float h-11 w-11 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: `${BRAND_ORANGE}15` }}>
            <Rocket className="h-5 w-5" style={{ color: BRAND_ORANGE }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-bold text-sm sm:text-base" style={{ color: BRAND_BLUE }}>
                Setup progress
                <Tip text="Complete every step to unlock the full Pulse-Agent experience. Auto-updates as you use the product." />
              </h3>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${BRAND_ORANGE}20`, color: BRAND_ORANGE }} data-testid="onboarding-progress-pill">
                {data.completed} / {data.total}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">You're {pct}% there. Keep going — it pays off fast.</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setExpanded(!expanded)} className="text-slate-500 hover:text-slate-800 p-1.5 rounded hover:bg-slate-100" data-testid="onboarding-toggle" aria-label="Toggle steps">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button onClick={dismiss} className="text-slate-400 hover:text-slate-700 p-1.5 rounded hover:bg-slate-100" data-testid="onboarding-dismiss" aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ORANGE})` }}
          data-testid="onboarding-progress-bar"
        />
      </div>

      {/* Steps grid */}
      {expanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
          {data.steps.map((s) => {
            const label = s.label || LABELS[s.key] || s.key;
            return (
              <button
                key={s.key}
                onClick={() => !s.done && goto(s.cta)}
                disabled={s.done}
                data-testid={`onboarding-step-${s.key}`}
                className={`flex items-center gap-2 p-2.5 rounded-lg border text-left text-sm transition ${s.done ? "bg-emerald-50 border-emerald-200 cursor-default" : "bg-white border-slate-200 hover:border-[#004a99] hover:bg-[#004a99]/05 cursor-pointer"}`}
              >
                {s.done
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  : <Circle className="h-5 w-5 flex-shrink-0" style={{ color: BRAND_BLUE }} />}
                <span className={`truncate ${s.done ? "text-emerald-900 font-medium line-through opacity-80" : "text-slate-800"}`}>{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
